import { api } from '@goaltree/backend/convex/_generated/api'
import { useAction, useMutation, useQuery } from 'convex/react'
import { CalendarDaysIcon, PlusIcon, RefreshCwIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export function CalendarSettings() {
  const { t } = useTranslation()
  const connections = useQuery(api.calendar.getConnectionStatus)
  const getAuthUrl = useAction(api.calendar.getAuthUrl)
  const disconnect = useMutation(api.calendar.disconnect).withOptimisticUpdate(
    (store, { googleEmail }) => {
      const cur = store.getQuery(api.calendar.getConnectionStatus, {})
      if (cur)
        store.setQuery(
          api.calendar.getConnectionStatus,
          {},
          cur.filter((c) => c.googleEmail !== googleEmail),
        )
    },
  )

  async function handleConnect() {
    try {
      const tzOffsetMinutes = new Date().getTimezoneOffset()
      const { url } = await getAuthUrl({ tzOffsetMinutes })
      window.location.href = url
    } catch {
      toast.error(t('settings.calendar.connectError'))
    }
  }

  async function handleDisconnect(googleEmail: string) {
    try {
      await disconnect({ googleEmail })
      toast.success(t('settings.calendar.disconnected'))
    } catch {
      toast.error(t('settings.calendar.disconnectError'))
    }
  }

  if (connections === undefined) {
    return (
      <div className="flex items-center gap-4 px-4 py-3.5">
        <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          <div className="h-4 w-4 rounded bg-muted-foreground/20 animate-pulse" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="h-4 w-40 rounded bg-muted animate-pulse" />
          <div className="mt-1.5 h-3 w-56 max-w-full rounded bg-muted/60 animate-pulse" />
        </div>
        <div className="h-7 w-20 shrink-0 rounded-lg bg-muted animate-pulse" />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {connections.map((conn) => (
        <div key={conn.id} className="flex items-center gap-4 px-4 py-3">
          <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {conn.status === 'requires_reauth' ? (
              <RefreshCwIcon className="h-4 w-4 text-warning" />
            ) : (
              <CalendarDaysIcon className="h-4 w-4" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              {conn.status === 'requires_reauth'
                ? t('settings.calendar.requiresReauth')
                : t('settings.calendar.connectedTitle')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('settings.calendar.description')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground/60 truncate">{conn.googleEmail}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {conn.status === 'requires_reauth' && (
              <button
                type="button"
                onClick={handleConnect}
                className="cursor-pointer rounded-lg bg-warning/15 px-3 py-1.5 text-xs font-medium text-warning transition-colors hover:bg-warning/25"
              >
                {t('settings.calendar.reconnect')}
              </button>
            )}

            <button
              type="button"
              onClick={() => void handleDisconnect(conn.googleEmail)}
              className="cursor-pointer text-muted-foreground/40 hover:text-muted-foreground transition-colors"
              title={t('settings.calendar.disconnect')}
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      {connections.length === 0 && (
        <div className="flex items-center gap-4 px-4 py-3">
          <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <CalendarDaysIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              {t('settings.calendar.notConnected')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('settings.calendar.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleConnect}
            className="cursor-pointer shrink-0 flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <PlusIcon className="h-3 w-3" strokeWidth={2.5} />
            {t('settings.calendar.connect')}
          </button>
        </div>
      )}
    </div>
  )
}
